"use client";

import RTHMIntelligenceLogo from "@/components/SVG/Logos/RTHMIntelligence";
import RTHMIntelligenceLogoVertical from "@/components/SVG/Logos/RTHMIntelligenceVertical";
import RTHMLogo from "@/components/SVG/Logos/RTHMLogo";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    NavigationMenu,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
    Sheet,
    SheetContent,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    FORMATTED_RTHM_PHONE_NUMBER,
    RTHM_PHONE_NUMBER,
} from "@/constants/Mappings";
import { cn } from "@/lib/utils";
import {
    MoonIcon,
    QuestionMarkCircleIcon,
    SunIcon,
} from "@heroicons/react/24/outline";
import {
    Bars3Icon,
    ChartBarSquareIcon,
    PowerIcon,
    UserIcon,
} from "@heroicons/react/24/solid";
import { Session } from "next-auth";
import { signOut } from "next-auth/react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import React, { useEffect, useState } from "react";

interface HeaderClientProps {
    session: Session | null;
    shadow?: boolean;
}

export default function HeaderClient({
    session,
    shadow = true,
}: HeaderClientProps) {
    const router = useRouter();
    const posthog = usePostHog();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    const { theme, setTheme } = useTheme();
    const pathname = usePathname();

    // Avoid hydration mismatch by only rendering theme toggle after component is mounted
    useEffect(() => {
        setMounted(true);
    }, []);

    const profileInitial = session?.user?.first_name
        ? session.user.first_name[0].toLocaleUpperCase() +
          (session.user.last_name
              ? session.user.last_name[0].toLocaleUpperCase()
              : "")
        : session?.user?.email
          ? session.user.email[0].toLocaleUpperCase()
          : "U";

    const logOut = async () => {
        posthog?.capture("sign_out");
        posthog?.reset();
        document.cookie =
            "visitorId-discover=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
            "visitorId-intelligence=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        localStorage.removeItem("visitorId-discover");
        localStorage.removeItem("visitorId-intelligence");
        await signOut({ redirectTo: "/", redirect: true });
    };

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isMenuOpen]);

    return (
        <header
            className={`sticky top-0 z-50 bg-popover ${shadow ? "shadow-md" : "border-b border-border"}`}
        >
            {/* Top Blue Bar */}
            <div className="w-full bg-rthm-dark-blue px-4 py-2 flex justify-center items-center text-white text-sm">
                <div className="flex items-center space-x-2">
                    <span className="opacity-95">Questions?</span>
                    <span className="opacity-95">
                        Call us at{" "}
                        <a
                            href={`tel:${FORMATTED_RTHM_PHONE_NUMBER}`}
                            className="underline font-medium hover:text-white transition-colors"
                            onClick={() => {
                                posthog?.capture("call_us_from_header");
                            }}
                        >
                            {RTHM_PHONE_NUMBER}
                        </a>
                    </span>
                </div>
            </div>

            {/* Main Header */}
            <div className="w-full px-4 lg:px-6 xl:px-8 py-3 flex justify-between items-center border-b border-border/80">
                {/* Logo and Desktop Navigation */}
                <div className="flex items-center space-x-4 lg:space-x-6">
                    <div
                        onClick={() => {
                            posthog?.capture("home_page_from_header");
                        }}
                    >
                        {pathname.includes("/intelligence") ? (
                            <>
                                <RTHMIntelligenceLogo className="md:hidden shrink-0" />
                                <RTHMIntelligenceLogoVertical className="hidden md:flex shrink-0" />
                            </>
                        ) : (
                            <>
                                {session && !session.user.canAccessDirect ? (
                                    <>
                                        <RTHMIntelligenceLogo className="md:hidden shrink-0" />
                                        <RTHMIntelligenceLogoVertical className="hidden md:flex shrink-0" />
                                    </>
                                ) : (
                                    <RTHMLogo link="/" className="shrink-0" />
                                )}
                            </>
                        )}
                    </div>

                    {/* Desktop Navigation Menu */}
                    <NavigationMenu className="hidden lg:flex">
                        <NavigationMenuList>
                            {/* <button
                                onClick={() =>
                                    setTheme(
                                        theme === "dark" ? "light" : "dark",
                                    )
                                }
                                className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5 w-full"
                                aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                            >
                                {mounted && theme === "dark" ? (
                                    <>
                                        <SunIcon className="w-5 h-5 text-yellow-300" />
                                        <span className="text-foreground text-lg font-normal font-space leading-[27px] ml-3">
                                            Light Mode
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <MoonIcon className="w-5 h-5 " />
                                        <span className="text-foreground text-lg font-normal font-space leading-[27px] ml-3">
                                            Dark Mode
                                        </span>
                                    </>
                                )}
                            </button> */}
                            {!pathname.includes("/intelligence") &&
                            (!session ||
                                (session && session.user.canAccessDirect)) ? (
                                <>
                                    {session?.user.id ? (
                                        <>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/dashboard"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "dashboard_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base font-medium transition-all duration-200", // Reduced font size with transition
                                                            pathname ===
                                                                "/dashboard"
                                                                ? "text-primary font-bold underline" // Active state
                                                                : "text-foreground/70 hover:text-primary/90",
                                                        )}
                                                    >
                                                        Dashboard
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/treatments"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "treatments_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base", // Reduced font size
                                                            pathname ===
                                                                "/treatments"
                                                                ? "text-primary font-bold underline"
                                                                : "text-foreground/80 hover:text-primary/90",
                                                        )}
                                                    >
                                                        Treatments
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/appointments"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "appointments_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base", // Reduced font size
                                                            pathname ===
                                                                "/appointments"
                                                                ? "text-primary font-bold underline"
                                                                : "text-foreground/80 hover:text-primary/90",
                                                        )}
                                                    >
                                                        Appointments
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                        </>
                                    ) : (
                                        <>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/treatments"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "treatments_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base", // Reduced font size
                                                            pathname ===
                                                                "/treatments"
                                                                ? "text-primary font-bold underline"
                                                                : "text-foreground/80 hover:text-primary/90",
                                                        )}
                                                    >
                                                        Treatments
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/how-it-works"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "how_it_works_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base", // Reduced font size
                                                            pathname ===
                                                                "/how-it-works"
                                                                ? "text-primary font-bold underline"
                                                                : "text-foreground/80 hover:text-primary/90",
                                                        )}
                                                    >
                                                        How It Works
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/resources"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_feedback_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base", // Reduced font size
                                                            pathname ===
                                                                "/resources"
                                                                ? "text-primary font-bold underline"
                                                                : "text-foreground/80 hover:text-primary/90",
                                                        )}
                                                    >
                                                        Resources
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href={
                                                            session?.user.id
                                                                ? "/intelligence/dashboard"
                                                                : "/intelligence"
                                                        }
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_dashboard_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-[#D97706] dark:text-[#F59E0B] transition-colors duration-200 text-base font-medium font-space leading-normal whitespace-nowrap flex items-center relative bg-transparent hover:bg-transparent", // Reduced font size, adjusted leading
                                                            // Apply active state if relevant, keeping intelligence styles prominent
                                                            (pathname.includes(
                                                                "/intelligence",
                                                            ) &&
                                                                !session?.user
                                                                    .id &&
                                                                pathname ===
                                                                    "/intelligence") ||
                                                                (pathname.includes(
                                                                    "/intelligence",
                                                                ) &&
                                                                    session
                                                                        ?.user
                                                                        .id &&
                                                                    pathname ===
                                                                        "/intelligence/dashboard")
                                                                ? "font-bold underline" // Active state: keep color, make bold
                                                                : "hover:opacity-80", // Inactive state hover
                                                        )}
                                                    >
                                                        <span className="relative">
                                                            <span className="absolute -top-4 -right-8 text-xs bg-gradient-to-r from-amber-500 to-amber-600 text-white px-1.5 py-0.5 rounded-sm shadow-sm">
                                                                BETA
                                                            </span>
                                                            RTHM Intelligence
                                                        </span>
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    {session?.user.id &&
                                    pathname !== "/intelligence/assessment" ? (
                                        <>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/intelligence/dashboard"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_dashboard_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base", // Reduced font size
                                                            pathname ===
                                                                "/intelligence/dashboard"
                                                                ? "text-primary font-bold underline"
                                                                : "text-foreground/80 hover:text-primary/90",
                                                        )}
                                                    >
                                                        Dashboard
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/intelligence/documents"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_documents_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base", // Reduced font size
                                                            pathname ===
                                                                "/intelligence/documents"
                                                                ? "text-primary font-bold underline"
                                                                : "text-foreground/80 hover:text-primary/90",
                                                        )}
                                                    >
                                                        Documents
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/intelligence/documents/labs"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_labs_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base", // Reduced font size
                                                            pathname ===
                                                                "/intelligence/documents/labs"
                                                                ? "text-primary font-bold underline"
                                                                : "text-foreground/80 hover:text-primary/90",
                                                        )}
                                                    >
                                                        Labs
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/intelligence/documents/chart-notes"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_chart_notes_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base", // Reduced font size
                                                            pathname ===
                                                                "/intelligence/documents/chart-notes"
                                                                ? "text-primary font-bold underline"
                                                                : "text-foreground/80 hover:text-primary/90",
                                                        )}
                                                    >
                                                        Chart Notes
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/intelligence/chat"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_ai_chat_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base", // Reduced font size
                                                            pathname ===
                                                                "/intelligence/chat"
                                                                ? "text-primary font-bold underline"
                                                                : "text-foreground/80 hover:text-primary/90",
                                                        )}
                                                    >
                                                        AI Chat
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                            <NavigationMenuItem>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="bg-popover"
                                                >
                                                    <Link
                                                        href="/intelligence/feedback"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_feedback_page_from_header",
                                                            );
                                                        }}
                                                        className={cn(
                                                            navigationMenuTriggerStyle(),
                                                            "text-base", // Reduced font size
                                                            pathname ===
                                                                "/intelligence/feedback"
                                                                ? "text-primary font-bold underline"
                                                                : "text-foreground/80 hover:text-primary/90",
                                                        )}
                                                    >
                                                        Feedback
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                        </>
                                    ) : (
                                        <NavigationMenuItem>
                                            <NavigationMenuLink
                                                asChild
                                                className="bg-popover"
                                            >
                                                <Link
                                                    href={
                                                        session?.user.id
                                                            ? "/dashboard"
                                                            : "/"
                                                    }
                                                    onClick={() => {
                                                        posthog?.capture(
                                                            "direct_dashboard_from_header",
                                                        );
                                                        setIsMenuOpen(false);
                                                    }}
                                                    className={cn(
                                                        navigationMenuTriggerStyle(),
                                                        "text-base", // Reduced font size
                                                        pathname ===
                                                            "/intelligence/feedback"
                                                            ? "text-primary font-bold underline"
                                                            : "text-foreground/80 hover:text-primary/90",
                                                    )}
                                                >
                                                    RTHM Direct
                                                </Link>
                                            </NavigationMenuLink>
                                        </NavigationMenuItem>
                                    )}
                                </>
                            )}
                        </NavigationMenuList>
                    </NavigationMenu>
                </div>

                {/* Mobile Menu Trigger */}
                <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                    <SheetTrigger asChild className="lg:hidden">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="flex items-center justify-center w-10 h-10 rounded-md hover:bg-accent/50 transition-all duration-200 focus:outline-hidden"
                            aria-label="Open Menu"
                        >
                            <Bars3Icon className="w-6 h-6 text-foreground/80" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[340px] p-0">
                        <SheetTitle className="sr-only">
                            Mobile Navigation Menu
                        </SheetTitle>
                        <div className="flex flex-col h-full pt-16 justify-between">
                            {/* Navigation Links */}
                            <nav className="px-6">
                                <div className="space-y-2">
                                    {!pathname.includes("/intelligence") ? (
                                        <>
                                            {session?.user.id ? (
                                                <>
                                                    <Link
                                                        href="/dashboard"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "dashboard_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Dashboard
                                                        </span>
                                                    </Link>
                                                    <Link
                                                        href="/treatments"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "treatments_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Treatments
                                                        </span>
                                                    </Link>
                                                    <Link
                                                        href="/appointments"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "appointments_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Appointments
                                                        </span>
                                                    </Link>
                                                    <Link
                                                        href="/support"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "support_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Support
                                                        </span>
                                                    </Link>
                                                    <div className="mt-4 border-t border-border pt-4">
                                                        <Link
                                                            href={
                                                                session?.user.id
                                                                    ? "/intelligence/dashboard"
                                                                    : "/intelligence"
                                                            }
                                                            onClick={() => {
                                                                posthog?.capture(
                                                                    "intelligence_dashboard_from_header",
                                                                );
                                                                setIsMenuOpen(
                                                                    false,
                                                                );
                                                            }}
                                                            className="flex items-center py-3 text-[#D97706] dark:text-[#F59E0B] hover:text-[#B45309] dark:hover:text-[#FBBF24] hover:border-[#B45309] dark:hover:border-[#FBBF24] rounded-lg px-4 transition-colors relative"
                                                        >
                                                            <span className="text-lg font-medium font-space leading-[27px] flex items-center relative">
                                                                <span className="absolute -top-3 -right-8 text-xs bg-gradient-to-r from-amber-500 to-amber-600 text-white px-1.5 py-0.5 rounded-sm shadow-sm">
                                                                    BETA
                                                                </span>
                                                                RTHM
                                                                Intelligence
                                                            </span>
                                                        </Link>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <Link
                                                        href="/treatments"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "treatments_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Treatments
                                                        </span>
                                                    </Link>
                                                    <Link
                                                        href="/how-it-works"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "how_it_works_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            How It Works
                                                        </span>
                                                    </Link>
                                                    <Link
                                                        href="/resources"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "resources_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Resources
                                                        </span>
                                                    </Link>
                                                    <Link
                                                        href="/support"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "support_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Support
                                                        </span>
                                                    </Link>
                                                    <div className="mt-4 border-t border-border pt-4">
                                                        <Link
                                                            href={
                                                                session?.user.id
                                                                    ? "/intelligence/dashboard"
                                                                    : "/intelligence"
                                                            }
                                                            onClick={() => {
                                                                posthog?.capture(
                                                                    "intelligence_dashboard_from_header",
                                                                );
                                                                setIsMenuOpen(
                                                                    false,
                                                                );
                                                            }}
                                                            className="flex items-center py-3 text-[#D97706] dark:text-[#F59E0B] hover:text-[#B45309] dark:hover:text-[#FBBF24] hover:border-[#B45309] dark:hover:border-[#FBBF24] rounded-lg px-4 transition-colors relative"
                                                        >
                                                            <span className="text-lg font-medium font-space leading-[27px] flex items-center relative">
                                                                <span className="absolute -top-3 -right-8 text-xs bg-gradient-to-r from-amber-500 to-amber-600 text-white px-1.5 py-0.5 rounded-sm shadow-sm">
                                                                    BETA
                                                                </span>
                                                                RTHM
                                                                Intelligence
                                                            </span>
                                                        </Link>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {session?.user.id ? (
                                                <>
                                                    <Link
                                                        href="/intelligence/dashboard"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_dashboard_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Dashboard
                                                        </span>
                                                    </Link>
                                                    <Link
                                                        href="/intelligence/documents"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_documents_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Documents
                                                        </span>
                                                    </Link>
                                                    <Link
                                                        href="/intelligence/documents/labs"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_labs_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Labs
                                                        </span>
                                                    </Link>
                                                    <Link
                                                        href="/intelligence/documents/chart-notes"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_chart_notes_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Chart Notes
                                                        </span>
                                                    </Link>
                                                    <Link
                                                        href="/intelligence/chat"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "intelligence_ai_chat_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            AI Chat
                                                        </span>
                                                    </Link>
                                                </>
                                            ) : (
                                                <>
                                                    <Link
                                                        href="/intelligence/support"
                                                        onClick={() => {
                                                            posthog?.capture(
                                                                "support_page_from_header",
                                                            );
                                                            setIsMenuOpen(
                                                                false,
                                                            );
                                                        }}
                                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                                    >
                                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                            Support
                                                        </span>
                                                    </Link>
                                                    <div className="mt-4 border-t border-border pt-4">
                                                        <Link
                                                            href={
                                                                session?.user.id
                                                                    ? "/dashboard"
                                                                    : "/"
                                                            }
                                                            onClick={() => {
                                                                posthog?.capture(
                                                                    "direct_dashboard_from_header",
                                                                );
                                                                setIsMenuOpen(
                                                                    false,
                                                                );
                                                            }}
                                                            className="flex items-center py-3rounded-lg px-4 transition-colors relative"
                                                        >
                                                            <span className="text-lg font-medium font-space leading-[27px] flex items-center relative">
                                                                RTHM Direct
                                                            </span>
                                                        </Link>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </nav>

                            {/* Profile and Sign Out for authenticated users */}
                            {session?.user && (
                                <div className="mt-auto w-full bg-background px-6 pb-4 pt-4">
                                    <div className="w-full h-[1px] bg-border mb-4"></div>
                                    <button
                                        onClick={() =>
                                            setTheme(
                                                theme === "dark"
                                                    ? "light"
                                                    : "dark",
                                            )
                                        }
                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5 w-full"
                                        aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                                    >
                                        {mounted && theme === "dark" ? (
                                            <>
                                                <SunIcon className="w-5 h-5 text-yellow-300" />
                                                <span className="text-foreground text-lg font-normal font-space leading-[27px] ml-3">
                                                    Light Mode
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <MoonIcon className="w-5 h-5 " />
                                                <span className="text-foreground text-lg font-normal font-space leading-[27px] ml-3">
                                                    Dark Mode
                                                </span>
                                            </>
                                        )}
                                    </button>
                                    {session.user.canAccessDirect &&
                                        pathname !== "/dashboard" && (
                                            <Link
                                                href="/dashboard"
                                                onClick={() => {
                                                    posthog?.capture(
                                                        "dashboard_page_from_header",
                                                    );
                                                    setIsMenuOpen(false);
                                                }}
                                                className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                            >
                                                <ChartBarSquareIcon className="w-5 h-5 mr-3" />
                                                <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                    Direct Dashboard
                                                </span>
                                            </Link>
                                        )}
                                    {pathname !== "/intelligence/dashboard" && (
                                        <Link
                                            href="/intelligence/dashboard"
                                            onClick={() => {
                                                posthog?.capture(
                                                    "intelligence_dashboard_page_from_header",
                                                );
                                                setIsMenuOpen(false);
                                            }}
                                            className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                        >
                                            <Bars3Icon className="w-5 h-5 mr-3" />
                                            <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                                Intelligence Dashboard
                                            </span>
                                        </Link>
                                    )}
                                    <Link
                                        href="/profile"
                                        onClick={() => {
                                            posthog?.capture(
                                                "profile_page_from_header",
                                            );
                                            setIsMenuOpen(false);
                                        }}
                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5"
                                    >
                                        <UserIcon className="w-5 h-5 mr-3" />
                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                            Profile
                                        </span>
                                    </Link>
                                    <button
                                        onClick={logOut}
                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5 w-full text-left"
                                        aria-label="Sign Out"
                                    >
                                        <PowerIcon className="w-5 h-5 mr-3" />
                                        <span className="text-foreground text-lg font-normal font-space leading-[27px]">
                                            Sign Out
                                        </span>
                                    </button>
                                </div>
                            )}

                            {/* Bottom Actions for non-authenticated users */}
                            {!session?.user && (
                                <div className="mt-auto w-full bg-background px-6 pb-4 pt-4">
                                    <div className="w-full h-[1px] bg-border mb-4"></div>
                                    <button
                                        onClick={() =>
                                            setTheme(
                                                theme === "dark"
                                                    ? "light"
                                                    : "dark",
                                            )
                                        }
                                        className="flex items-center py-3 text-foreground hover:bg-accent/60 rounded-lg px-4 transition-all duration-200 hover:pl-5 w-full mb-4"
                                        aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                                    >
                                        {mounted && theme === "dark" ? (
                                            <>
                                                <SunIcon className="w-5 h-5 mr-6 text-yellow-400" />
                                                <span className="text-foreground text-lg font-normal font-space leading-[27px] ml-3">
                                                    Light Mode
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <MoonIcon className="w-5 h-5 mr-6 text-foreground/80" />
                                                <span className="text-foreground text-lg font-normal font-space leading-[27px] ml-3">
                                                    Dark Mode
                                                </span>
                                            </>
                                        )}
                                    </button>
                                    <Button
                                        id="log-in-from-header-mobile"
                                        variant="outline"
                                        className="w-full uppercase mb-3 rounded-full"
                                        onClick={() => {
                                            posthog?.capture(
                                                "log_in_page_from_header",
                                            );
                                            router.push(
                                                `/sign-in?redirectTo=${pathname}`,
                                            );
                                            setIsMenuOpen(false);
                                        }}
                                    >
                                        Log In
                                    </Button>
                                    {!pathname.includes("/intelligence") && (
                                        <Button
                                            id="get-started-from-header-mobile"
                                            variant="default"
                                            className="w-full uppercase rounded-full bg-rthm-dark-blue dark:text-white"
                                            onClick={() => {
                                                posthog?.capture(
                                                    "discover_page_from_header",
                                                );
                                                router.push("/discover");
                                                setIsMenuOpen(false);
                                            }}
                                        >
                                            Get Started
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </SheetContent>
                </Sheet>

                {/* Desktop Buttons/User Menu */}
                <div className="hidden lg:flex items-center space-x-4 xl:space-x-5">
                    <Link
                        href={
                            pathname.includes("/intelligence")
                                ? "/intelligence/support"
                                : "/support"
                        }
                        onClick={() => {
                            posthog?.capture("support_page_from_header");
                        }}
                        className="flex items-center justify-center"
                        aria-label="Support"
                    >
                        <QuestionMarkCircleIcon className="h-6 w-6 text-foreground/80 hover:text-primary transition-colors duration-200" />
                    </Link>
                    {session?.user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="p-0 h-auto">
                                    <Avatar className="h-6 w-6 bg-rthm-dark-blue">
                                        <AvatarFallback className="text-xs font-medium bg-rthm-dark-blue text-white">
                                            {profileInitial}
                                        </AvatarFallback>
                                    </Avatar>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="w-56 mt-1 border border-border/80 shadow-lg rounded-lg overflow-hidden"
                            >
                                <DropdownMenuItem
                                    onClick={() =>
                                        setTheme(
                                            theme === "dark" ? "light" : "dark",
                                        )
                                    }
                                >
                                    {mounted && theme === "dark" ? (
                                        <div className="flex items-center">
                                            <SunIcon className="w-5 h-5 mr-6 text-yellow-400" />
                                            Light Mode
                                        </div>
                                    ) : (
                                        <div className="flex items-center">
                                            <MoonIcon className="w-5 h-5 mr-6 text-foreground/80" />
                                            Dark Mode
                                        </div>
                                    )}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {session.user.canAccessDirect &&
                                    pathname !== "/dashboard" && (
                                        <DropdownMenuItem asChild>
                                            <Link
                                                href="/dashboard"
                                                onClick={() => {
                                                    posthog?.capture(
                                                        "dashboard_page_from_header",
                                                    );
                                                }}
                                                className="flex items-center cursor-pointer"
                                            >
                                                <ChartBarSquareIcon className="w-5 h-5 mr-4 text-foreground/80" />
                                                Direct Dashboard
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                {pathname !== "/intelligence/dashboard" && (
                                    <DropdownMenuItem asChild>
                                        <Link
                                            href="/intelligence/dashboard"
                                            onClick={() => {
                                                posthog?.capture(
                                                    "intelligence_dashboard_page_from_header",
                                                );
                                            }}
                                            className="flex items-center cursor-pointer"
                                        >
                                            <Bars3Icon className="w-5 h-5 mr-4 text-foreground/80" />
                                            Intelligence Dashboard
                                        </Link>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem asChild>
                                    <Link
                                        href="/profile"
                                        onClick={() => {
                                            posthog?.capture(
                                                "profile_page_from_header",
                                            );
                                        }}
                                        className="flex items-center cursor-pointer"
                                    >
                                        <UserIcon className="w-5 h-5 mr-4 text-foreground/80" />
                                        Profile
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={logOut}
                                    className="cursor-pointer"
                                >
                                    <PowerIcon className="w-5 h-5 mr-4 text-foreground/80" />
                                    Sign Out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <div className="flex items-center gap-3 xl:gap-4">
                            <Button
                                id="log-in-from-header-desktop"
                                variant="outline"
                                className="w-40 h-9 uppercase dark:border-2 rounded-full text-sm font-medium hover:bg-accent/50 transition-all duration-200" // Smaller and more refined
                                onClick={() => {
                                    posthog?.capture("log_in_page_from_header");
                                    router.push(
                                        `/sign-in?redirectTo=${pathname}`,
                                    );
                                }}
                            >
                                Log In
                            </Button>
                            {!pathname.includes("/intelligence") && (
                                <Button
                                    id="get-started-from-header-desktop"
                                    variant="default"
                                    className="w-40 h-9 whitespace-nowrap uppercase rounded-full text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 bg-rthm-dark-blue dark:text-white" // Smaller with shadow effect
                                    onClick={() => {
                                        posthog?.capture(
                                            "discover_page_from_header",
                                        );
                                        router.push("/discover");
                                    }}
                                >
                                    Get Started
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
